import React from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { CheckCircle2, Globe, Home, ShieldCheck } from 'lucide-react';
export function WhoWeAre() {
  return <div className="pt-28 pb-24" data-id="element-1452">
      <PageHeader eyebrow="About Us" title="Who We Are" subtitle="Pioneering accessible real estate and property ownership across Africa." breadcrumb={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Who We Are'
    }]} data-id="element-1453" />

      <div className="max-w-[1280px] mx-auto px-6 md:px-12 mt-16" data-id="element-1454">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1455">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="space-y-6 text-ink/80 text-lg leading-relaxed" data-id="element-1456">
            <p data-id="element-1457">
              <strong className="text-ink font-semibold" data-id="element-1458">Zylus Homes</strong> is
              a cutting-edge real estate and investment company dedicated to
              expanding access to home and property ownership in Africa. With
              over 5 years of credibility, we offer excellent services to
              clients across the globe.
            </p>
            <p data-id="element-1459">
              We handle the entire real estate lifecycle—from land acquisition
              and landbanking, to property development and construction
              management—empowering clients to make smarter buying and
              investment decisions.
            </p>
            <p data-id="element-1460">
              At Zylus, we take pride in satisfying our clients' ongoing needs,
              anchored in our core value:{' '}
              <strong className="text-gold-dark font-semibold" data-id="element-1461">
                integrity
              </strong>
              .
            </p>
            <p data-id="element-1462">
              Our success stories have been featured in reputable publications
              such as Punch and Vanguard. We exist to put smiles on our
              customers' faces.
            </p>

            <div className="pt-6 grid grid-cols-2 gap-6" data-id="element-1463">
              <div className="flex items-start gap-3" data-id="element-1464">
                <Globe className="w-6 h-6 text-gold shrink-0" data-id="element-1465" />
                <div data-id="element-1466">
                  <div className="font-semibold text-ink text-base mb-1" data-id="element-1467">
                    Global Reach
                  </div>
                  <div className="text-sm text-ink/60" data-id="element-1468">
                    Serving clients across the diaspora.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1469">
                <ShieldCheck className="w-6 h-6 text-gold shrink-0" data-id="element-1470" />
                <div data-id="element-1471">
                  <div className="font-semibold text-ink text-base mb-1" data-id="element-1472">
                    Integrity First
                  </div>
                  <div className="text-sm text-ink/60" data-id="element-1473">
                    Transparent processes and verified titles.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Decorative Graphic */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="relative h-[500px] bg-white rounded-3xl border border-ink/5 shadow-sm flex items-center justify-center p-12 overflow-hidden" data-id="element-1474">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#F5B800_0%,transparent_70%)] opacity-5" data-id="element-1475" />

            {/* Abstract Africa SVG */}
            <svg viewBox="0 0 100 100" className="w-full h-full text-bone drop-shadow-xl relative z-10" fill="currentColor" data-id="element-1476">
              <path d="M45,10 C55,12 65,20 70,30 C75,40 85,45 90,55 C92,65 85,75 75,85 C65,95 50,95 40,85 C30,75 25,60 20,50 C15,40 10,30 20,20 C30,10 35,8 45,10 Z" data-id="element-1477" />
            </svg>

            {/* Floating nodes */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity
          }} className="absolute top-1/4 left-1/4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center z-20" data-id="element-1478">
              <Home className="w-5 h-5 text-gold" data-id="element-1479" />
            </motion.div>
            <motion.div animate={{
            y: [0, 15, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            delay: 1
          }} className="absolute bottom-1/3 right-1/4 w-16 h-16 bg-ink rounded-full shadow-lg flex items-center justify-center z-20" data-id="element-1480">
              <span className="text-white font-serif font-bold text-xl" data-id="element-1481">Z</span>
            </motion.div>
            <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 3.5,
            repeat: Infinity,
            delay: 2
          }} className="absolute top-1/3 right-1/3 w-10 h-10 bg-gold rounded-full shadow-lg flex items-center justify-center z-20" data-id="element-1482">
              <CheckCircle2 className="w-5 h-5 text-white" data-id="element-1483" />
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-24 bg-ink rounded-3xl p-10 md:p-16 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 text-center divide-x divide-white/10" data-id="element-1484">
          <div data-id="element-1485">
            <div className="text-4xl md:text-5xl font-serif text-gold mb-2" data-id="element-1486">
              5+
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/70" data-id="element-1487">
              Years Credibility
            </div>
          </div>
          <div data-id="element-1488">
            <div className="text-4xl md:text-5xl font-serif text-gold mb-2" data-id="element-1489">
              1000+
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/70" data-id="element-1490">
              Happy Clients
            </div>
          </div>
          <div data-id="element-1491">
            <div className="text-4xl md:text-5xl font-serif text-gold mb-2" data-id="element-1492">
              6+
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/70" data-id="element-1493">
              Premium Estates
            </div>
          </div>
          <div data-id="element-1494">
            <div className="text-4xl md:text-5xl font-serif text-gold mb-2" data-id="element-1495">
              4
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-white/70" data-id="element-1496">
              Subsidiaries
            </div>
          </div>
        </motion.div>

        {/* Featured In */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mt-20 text-center" data-id="element-1497">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-ink/40 mb-8" data-id="element-1498">
            Featured In
          </div>
          <div className="flex items-center justify-center gap-12 md:gap-24 opacity-60 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-1499">
            <div className="text-2xl md:text-3xl font-serif font-bold text-ink" data-id="element-1500">
              PUNCH
            </div>
            <div className="text-2xl md:text-3xl font-serif font-bold text-ink" data-id="element-1501">
              VANGUARD
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}